import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import styles from './CarTimeline.styles';
import TimelineItem from '../TimeLine/TimelineItem';
import NowItem from '../../components/CarEditDrawer/NowItem';
import LoadMoreCarEvents from './LoadMoreCarEvents';
import { isBefore } from 'date-fns';
import { FILTER_VEHICLE_EVENTS_MODAL } from './FilterVehicleEventsModal';
import TimeTag from '../TimeLine/TimeTag';
import moment from 'moment';
import {
  selectSelectedCar,
  setCarDrawerActiveTab,
  carDamages as selectCarDamages,
} from '../../redux/store/userData/cars';
import {
  selectCarEventsFilters,
  selectCarEvents,
  setCarEventsFilters,
  fetchVehicleEvents,
} from '../../redux/store/userData/vehicleEvents';
import { openModal } from '../../redux/store/ui/common';
import { Checkbox } from 'antd';
import ClaimTimeline from '../../components/ClaimDrawer/ClaimTimeline';
import { selectSelectedClaim } from '../../redux/store/userData/claims';
import { fetchDamageEventsForCarvalooDamages } from '../../redux/store/userData/claims/thunk';
import { selectToken } from '../../redux/store/userData/user';

const CarTimeline: React.FC = () => {
  const dispatch = useDispatch();
  const token = useSelector(selectToken);
  const selectedClaim = useSelector(selectSelectedClaim);
  const [isGroupedByTrip, setIsGroupedByTrip] = useState(!!selectedClaim);
  const vehicleEvents = useSelector(selectCarEvents) || [];
  const selectedCar = useSelector(selectSelectedCar);
  const damages = useSelector(selectCarDamages);
  const carEventsFilters = useSelector(selectCarEventsFilters);

  useEffect(() => {
    if (damages && damages.length) {
      // @ts-ignore — thunk dispatch
      dispatch(fetchDamageEventsForCarvalooDamages(damages));
    }
  }, [dispatch, damages]);

  const visibleTripIds = new Set<string>();
  (vehicleEvents || []).forEach((e: any) => {
    if (e?.tripId) visibleTripIds.add(e.tripId);
  });

  const carvalooFeedbackEvents =
    carEventsFilters?.includes('Claims') && damages && visibleTripIds.size > 0
      ? damages.flatMap((d: any) => {
          const dTripId = d?.assignedTripId || d?.tripId;
          if (!dTripId || !visibleTripIds.has(dTripId)) return [];
          const dClaimedAt = d?.addedAt ? new Date(d.addedAt).getTime() : null;
          return (d?.events || [])
            .filter((e: any) => e?.type === 'feedback')
            .filter((e: any) => {
              if (dClaimedAt == null) return true;
              const t = new Date(e?.created).getTime();
              return !Number.isNaN(t) && t >= dClaimedAt;
            })
            .map((e: any) => ({
              type: 'carvalooFeedback',
              addedAt: e.created,
              damageId: d.damageId,
              damageEntityId: d.id,
              geoLocation: d.geoLocation,
              mileage: d.mileage,
              // Always use our S3 copy — carvaloo blob URLs expire.
              images: d.damageImages,
              eventInfo: e.comments,
              carvalooEventUrl: d.carvalooEventUrl,
            }));
        })
      : [];

  const noteAddedEvents =
    selectedCar && vehicleEvents && carEventsFilters?.includes('BaseData')
      ? selectedCar?.notes
          ?.filter((note) =>
            isBefore(
              // @ts-ignore
              new Date(vehicleEvents[vehicleEvents?.length - 1]?.addedAt),
              // @ts-ignore
              new Date(note.timestamp)
            )
          )
          .map((note) => ({
            type: note?.file ? 'documentAdded' : 'noteAdded',
            addedAt: note.timestamp,
            text: note.text,
            // @ts-ignore
            author: note?.author,
            ...note,
          }))
      : [];
  // @ts-ignore
  const mergedVehicleEvents = vehicleEvents
    .concat(noteAddedEvents)
    // @ts-ignore
    .concat(carvalooFeedbackEvents);
  mergedVehicleEvents?.sort(function (a, b) {
    const dateA = new Date(a.addedAt!),
      dateB = new Date(b.addedAt!);
    // @ts-ignore
    return dateB - dateA;
  });

  const handleFilterButton = (e: { preventDefault: () => void }) => {
    e.preventDefault();
    dispatch(openModal(FILTER_VEHICLE_EVENTS_MODAL));
  };

  const onClickGroupBy = (e: {
    target: { checked: boolean | ((prevState: boolean) => boolean) };
  }) => {
    setIsGroupedByTrip(e?.target?.checked);
    if (e?.target?.checked) {
      const from = moment().subtract(7, 'days').toISOString();
      const to = moment().toISOString();
      dispatch(setCarEventsFilters(['Claims']));
      dispatch(setCarDrawerActiveTab(1));
      dispatch(
        fetchVehicleEvents(
          token,
          selectedCar?.id!,
          from,
          to,
          ['damageClaim'],
          'CLAIM',
          false
        )
      );
    }
  };

  return (
    <>
      <div className="timeline-wrapper">
        <div
          style={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            width: '100%',
            boxSizing: 'border-box',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            borderBottom: '1px solid rgba(0, 0, 0, 0.2)',
            padding: '4px',
            backgroundColor: 'white',
          }}
        >
          <Checkbox
            checked={isGroupedByTrip}
            onChange={onClickGroupBy}
            data-testid="CarTimeline-checkbox-group-by-trip"
          >
            Group by trip
          </Checkbox>
          <div className="timeline-filter-button" onClick={handleFilterButton}>
            Filters
          </div>
        </div>
        {isGroupedByTrip ? (
          <ClaimTimeline />
        ) : (
          <>
            {mergedVehicleEvents && <NowItem key={`vehicleEvents-now`} />}
            {mergedVehicleEvents?.map((ve, _i) => {
              return (
                <>
                  {(_i === 0 ||
                    (ve?.addedAt &&
                      !moment(ve?.addedAt)?.isSame(
                        mergedVehicleEvents[_i - 1].addedAt,
                        'day'
                      ))) && <TimeTag date={ve?.addedAt!} />}
                  {ve && (
                    <TimelineItem
                      key={`vehicleEvents-${_i}`}
                      vehicleEvent={ve}
                      hideSecondBar={_i === mergedVehicleEvents.length - 1}
                    />
                  )}
                </>
              );
            })}
          </>
        )}
        {!isGroupedByTrip && <LoadMoreCarEvents />}
      </div>
      <style jsx>{styles}</style>
    </>
  );
};

export default CarTimeline;
